/**
 * localNotify.js — fire a browser notification from the page.
 *
 * "Local" means the page itself raises it, as opposed to an FCM web-push sent
 * by the server: no VAPID key, no service-worker round trip, and it works
 * offline. The trade-off is that the tab has to be running — a local
 * notification can appear while the tab is in the *background*, but not after
 * the browser is closed. Anything that must reach a closed device belongs in
 * the FCM path in fcmUtils.js instead.
 *
 * Every call is best-effort: notifications are a convenience, never a step the
 * caller's logic depends on, so nothing here throws.
 */

/** 'unsupported' | 'granted' | 'denied' | 'default' */
export function notifyPermission() {
  if (typeof window === 'undefined' || !('Notification' in window)) return 'unsupported';
  return Notification.permission;
}

export const canNotify = () => notifyPermission() === 'granted';

/**
 * Ask for permission, returning the resulting state. Browsers only show the
 * prompt in response to a user gesture, so call this from a click.
 */
export async function requestNotifyPermission() {
  if (notifyPermission() === 'unsupported') return 'unsupported';
  try {
    return await Notification.requestPermission();
  } catch {
    return Notification.permission;
  }
}

/**
 * Show a notification. `tag` replaces any earlier notification with the same
 * tag rather than stacking a second copy, which is what you want when a
 * reminder could be raised twice.
 *
 * Prefers the service worker registration: on Android and installed PWAs a
 * plain `new Notification()` throws, and only the SW form is allowed.
 */
export function showLocalNotification(title, body, { tag, url, silent = false } = {}) {
  try {
    if (!canNotify()) return false;
    const options = {
      body,
      tag,
      icon: '/favicon.ico',
      badge: '/favicon.ico',
      silent,
      vibrate: [120, 60, 120], // ignored on desktop, useful on a phone
      data: { url: url || (typeof window !== 'undefined' ? window.location.href : '') },
    };
    if (typeof navigator !== 'undefined' && navigator.serviceWorker?.ready) {
      navigator.serviceWorker.ready
        .then(reg => reg.showNotification(title, options))
        .catch(() => { try { new Notification(title, options); } catch { /* nothing more to try */ } });
    } else {
      new Notification(title, options);
    }
    return true;
  } catch {
    return false;
  }
}
